/*
 * Copyright (C) 2017 Spanning Cloud Apps.  All rights reserved.
 */
import './metadataConfirmView.tpl.html';

angular
  .module('bulkRestore.metadataConfirmView', ['shared.filters'])
  /**
   * Controller for the bulkRestore.metadataConfirmView template.
   */
  .controller('BulkRestoreMetadataConfirmViewCtrl', [
    '$filter',
    '$q',
    '$rootScope',
    '$scope',
    '$state',
    'batchUtils',
    'httpQueue',
    'progressFactory',
    'BulkRestoreService',
    'WizardService',
    'ALERT_EVENTS',
    'confirmModalUtils',
    function (
      $filter,
      $q,
      $rootScope,
      $scope,
      $state,
      batchUtils,
      httpQueue,
      progressFactory,
      BulkRestoreService,
      WizardService,
      ALERT_EVENTS,
      confirmModalUtils
    ) {
      if (!$state.params.bulkRestoreData) {
        throw new Error('bulkRestoreData is required');
      }

      var i18n = $filter('i18n');
      var pluralize = $filter('pluralize');
      var momentFilter = $filter('momentFilter');
      var momentFormatKey = 'M_D_Y_H_M';
      var bulkRestoreData = $state.params.bulkRestoreData;
      var itemsStr, momentStr, orgStr;

      // Publish functions in scope
      $scope.getExpanderClass = getExpanderClass;
      $scope.toggleTypeExpansion = toggleTypeExpansion;

      // Register our page with the wizard service
      var page = WizardService.getCurrentPage();
      page.setHeaderText(i18n('CONFIRM_CHANGES'));
      page.setStateParams($state.params);
      page.setValid(isValid);
      // For the purposes of confirming cancellation of the wizard we'll consider the wizard touched if we get this far.
      page.setTouched(true);

      // Add a custom button to the wizard to handle Restore
      var restoreButton = page.addCustomButton();
      restoreButton.setTextKey('RESTORE');
      restoreButton.setTooltipKey('RESTORE_NOW');
      restoreButton.setEnabled(function () {
        return !progressFactory.metadataRestoreInProgress;
      });
      restoreButton.setClick(() => {
        return confirmModalUtils
          .openConfirmModal({
            titleTextKey: 'CONTINUE',
            confirmTextKey: 'METADATA_RESTORE_CONFIRMATION'
          })
          .then((isConfirmed) => {
            if (isConfirmed) {
              return restore();
            }

            return false;
          });
      });

      $scope.bulkRestoreData = bulkRestoreData;

      countItems();

      // Initialize messages
      $scope.componentTypesMsg = pluralize(
        $scope.typeCount,
        'ONE_METADATA_COMPONENT_TYPE',
        'N_METADATA_COMPONENT_TYPES'
      );

      itemsStr = pluralize($scope.itemCount, 'ITEM', 'ITEMS');

      if (bulkRestoreData.isCrossOrgRestore()) {
        orgStr = bulkRestoreData.getDestOrgName() + ' (' + bulkRestoreData.getDestOrgSfId() + ')';
      } else {
        orgStr = i18n('THIS_ORG').toLowerCase();
      }

      $scope.restoreMsg = i18n('RESTORING_METADATA_ITEMS_TO_ORG', [$scope.itemCount, itemsStr, orgStr]);

      if (bulkRestoreData.isRestoreFromMetadataCompare()) {
        momentStr = momentFilter(bulkRestoreData.getBackupBatchDate(), momentFormatKey);
        $scope.backupMsg = i18n('RESTORING_FROM_BACKUP', momentStr);

        momentStr = momentFilter(bulkRestoreData.getCompareBatchDate(), momentFormatKey);
        $scope.compareMsg = i18n('RESTORING_FROM_COMPARE', momentStr);
      } else {
        momentStr = momentFilter(batchUtils.currentBatch.date, momentFormatKey);
        $scope.backupMsg = i18n('RESTORING_FROM_BACKUP', momentStr);
      }

      // Show the loading mask during server requests
      $scope.$watch(
        function () {
          return batchUtils.requestsInProgress;
        },
        function (newVal, oldVal) {
          if (newVal !== oldVal) {
            $rootScope.showLoadingMask = newVal > 0;
          }
        }
      );

      /**
       * Determines whether the settings on the page are valid (i.e. Next is allowed in the wizard)
       *
       * @returns {boolean} true if the settings are valid, false otherwise.
       */
      function isValid() {
        // Valid as long as no other restore is in progress
        return !progressFactory.metadataRestoreInProgress;
      }

      /**
       * Performs the restore.
       *
       * @returns {Promise} a promise that is resolved with one of the following:
       *   1) true if the wizard can be dismissed
       *   2) false if the wizard should remain
       *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
       */
      function restore() {
        var metadataObjects, params, paths;

        // Build the structure describing which items to restore. This is an object whose keys are the component types
        // to be restored. For each component type the value will either be 'ALL' or an array of specific metadata item
        // paths.
        metadataObjects = {};
        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          if (type.isSelected) {
            metadataObjects[type.componentType] = ['ALL'];
          } else if (type.selectedItemCount > 0) {
            paths = [];
            _.each(type.items, function (item) {
              if (item.isSelected) {
                paths.push(item.path);
              }
            });
            metadataObjects[type.componentType] = paths;
          }
        });

        params = {
          sourceSfOrgId: bulkRestoreData.getSourceOrgSfId(),
          targetSfOrgId: bulkRestoreData.getDestOrgSfId(),
          sourceBatchId: bulkRestoreData.getBackupBatchId(),
          metadataObjects: metadataObjects,
          ignoreWarnings: bulkRestoreData.isIgnoreWarnings()
        };

        // If this restore is from a compare, the batch id in bulkRestoreData is the compare batch. We need the
        // backup batch id.
        if (bulkRestoreData.isRestoreFromMetadataCompare()) {
          params.metadataCompareId = bulkRestoreData.getCompareBatchId();
        }

        $rootScope.showLoadingMask = true;

        // Queue up the metadata restore. The response handler code was copied from bulkRestore/confirm/confirm.js
        return httpQueue
          .submitMetadataRestore(params)
          .then(handleResponse)
          .catch(handleError)
          .finally(function () {
            $rootScope.showLoadingMask = false;
          });

        /**
         * Handles a response from submitting the operation.
         *
         * @param {object} res - the response object.
         * @returns {boolean|object} one of the following:
         *   1) true if the wizard can be dismissed
         *   2) false if the wizard should remain
         *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
         */
        function handleResponse(res) {
          // Status of 1 indicates a failure
          if (res.status === 1) {
            return handleError(res.message);
          }

          // The operation succeeded
          progressFactory.metadataRestoreInProgress = true;

          clearSelection();

          return true; // We're done with the wizard
        }

        /**
         * Handles an error response from the metadata restore.
         *
         * @param {String} msg - The response message
         * @returns {boolean|object} one of the following:
         *   1) false if the wizard should remain
         *   2) an object containing the parameters to pass to the wizard cancel function to display an error message.
         */
        function handleError(msg) {
          // If the error message contains access_token_owner_id, we know that access_token_owner_id on auth_info
          // does not match access_token_owner_id that created the auth row. Handle differently by showing modal.
          if (msg && msg.indexOf('access_token_owner_id') >= 0) {
            // Return parameters to indicate we need an error popup
            return {
              showConfirm: true,
              confirmTextKey: 'ACCESS_TOKEN_OWNER_ID_ERROR',
              confirmButtonTextKey: 'GO_TO_DASHBOARD',
              extraHeight: true
            };
          }

          // We don't recognize the message
          var failureMsg = i18n('BULK_RESTORE_SUBMIT_FAILED');
          $rootScope.$emit(ALERT_EVENTS.ERROR.eventName, failureMsg);
          return false; // Didn't succeed, but we don't need the wizard to display the message.
        }
      }

      /**
       * Counts the number of component types and metadata items to be restored
       */
      function countItems() {
        $scope.typeCount = 0;
        $scope.itemCount = 0;

        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          if (type.isSelected) {
            $scope.typeCount++;
            $scope.itemCount += type.total;
          } else if (type.selectedItemCount > 0) {
            // Individual items are selected
            $scope.typeCount++;
            $scope.itemCount += type.selectedItemCount;
          }
        });
      }

      /**
       * Returns the class to be used to style a component type expander
       *
       * @param {Object} record - the component type record
       * @returns {String} - the CSS class(es)
       */
      function getExpanderClass(record) {
        var dirClass = record.isExpanded ? 'sbsf-log-header-asc' : 'sbsf-log-header-desc';
        return dirClass + ' sca-icon-down-black';
      }

      /**
       * Toggles the expanded state for a component type record
       *
       * @param {Object} type - the component type record
       * @returns {Promise} - a promise that is resolved when the metadata items are loaded
       */
      function toggleTypeExpansion(type) {
        type.isExpanded = !type.isExpanded;

        // Load the backups for this component type if not already loaded
        if (type.isExpanded && !type.items) {
          $rootScope.showLoadingMask = true;

          return BulkRestoreService.getMetadataForBatchAndComponentType(
            bulkRestoreData.getBackupBatchId(),
            type.componentType
          )
            .then(function (items) {
              type.items = _.clone(items);

              // Mark the items as selected
              _.each(type.items, function (item) {
                item.isSelected = true;
              });
            })
            .finally(function () {
              $rootScope.showLoadingMask = false;
            });
        } else {
          return $q.when();
        }
      }

      /**
       * Resets the selection state of all metadata items after a restore was successfully submitted. We do this because
       * the metadata records remain in the BulkRestoreService cache for use during the next restore and we don't want
       * the selection to carry over.
       */
      function clearSelection() {
        _.each(bulkRestoreData.getMetadataRecords(), function (type) {
          type.isExpanded = false;
          type.isSelected = false;
          type.selectedItemCount = 0;

          _.each(type.items, function (item) {
            item.isSelected = false;
          });
        });
      }

      $scope.isIgnoreWarnings = bulkRestoreData.isIgnoreWarnings;
    }
  ]);
